import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import { Shield, Search, RefreshCw, User, Clock, Activity } from 'lucide-react';
import api from '@/utils/api';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [limit, setLimit] = useState(100);

  useEffect(() => { fetchData(); }, [filterAction, limit]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (filterAction) params.entity_type = filterAction;
      const [logsRes, statsRes] = await Promise.all([
        api.get(`${API}/audit-logs`, { params }),
        api.get(`${API}/audit-logs/stats`),
      ]);
      setLogs(logsRes.data);
      setStats(statsRes.data);
    } catch (e) { toast.error('Failed to load audit logs'); }
    finally { setLoading(false); }
  };

  const filtered = search
    ? logs.filter(l => l.user_name?.toLowerCase().includes(search.toLowerCase()) || l.action?.toLowerCase().includes(search.toLowerCase()) || l.entity_id?.toLowerCase().includes(search.toLowerCase()))
    : logs;

  const actionColor = (action) => {
    if (action?.includes('CREATE') || action?.includes('IMPORT')) return 'bg-green-100 text-green-700';
    if (action?.includes('DELETE') || action?.includes('REJECT')) return 'bg-red-100 text-red-700';
    if (action?.includes('UPDATE') || action?.includes('STATUS')) return 'bg-blue-100 text-blue-700';
    if (action?.includes('LOGIN')) return 'bg-slate-100 text-slate-700';
    return 'bg-amber-100 text-amber-700';
  };

  return (
    <CollapsibleLayout>
      <div className="min-h-[calc(100vh-48px)] bg-slate-50">
        <header className="bg-slate-900 text-white px-6 py-3 sticky top-12 z-40">
          <div className="flex items-center justify-between max-w-[1400px] mx-auto">
            <div className="flex items-center gap-3">
              <Shield size={20} className="text-amber-400" />
              <div>
                <h1 className="text-lg font-semibold" data-testid="audit-page-title">Audit Log</h1>
                <p className="text-slate-400 text-xs">System activity and security events</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="bg-transparent border-slate-600 text-white hover:bg-slate-800 h-7 text-xs">
              <RefreshCw size={13} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </header>

        <main className="max-w-[1400px] mx-auto p-6">
          {stats && (
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-lg border p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100"><Activity size={18} className="text-blue-600" /></div>
                  <div><p className="text-2xl font-bold">{stats.total}</p><p className="text-xs text-muted-foreground">Total Events</p></div>
                </div>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-100"><Clock size={18} className="text-green-600" /></div>
                  <div><p className="text-2xl font-bold">{stats.last_24h}</p><p className="text-xs text-muted-foreground">Last 24 Hours</p></div>
                </div>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <div className="text-xs space-y-1">
                  <p className="font-semibold text-foreground mb-1">Top Actions</p>
                  {stats.by_action?.slice(0, 4).map(a => (
                    <div key={a.action} className="flex justify-between"><span className="text-muted-foreground">{a.action}</span><span className="font-mono">{a.count}</span></div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-xs">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search user, action, entity..." className="pl-8 h-8 text-sm" data-testid="audit-search" />
            </div>
            <select value={filterAction} onChange={e => setFilterAction(e.target.value)} className="h-8 px-2 text-sm border rounded-md bg-white">
              <option value="">All Types</option>
              <option value="task">Tasks</option>
              <option value="user">Users</option>
              <option value="portal">Portal</option>
            </select>
            <select value={limit} onChange={e => setLimit(parseInt(e.target.value))} className="h-8 px-2 text-sm border rounded-md bg-white">
              <option value={50}>Last 50</option>
              <option value={100}>Last 100</option>
              <option value={500}>Last 500</option>
            </select>
          </div>

          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
                  <th className="text-left px-3 py-2 w-40">Timestamp</th>
                  <th className="text-left px-3 py-2">User</th>
                  <th className="text-left px-3 py-2">Action</th>
                  <th className="text-left px-3 py-2">Entity</th>
                  <th className="text-left px-3 py-2">Details</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">Loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No audit logs</td></tr>
                ) : filtered.map((log, i) => (
                  <tr key={i} className="border-b border-border/20 hover:bg-muted/20">
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap font-mono">
                      {log.timestamp ? new Date(log.timestamp).toLocaleString() : '\u2014'}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <User size={12} className="text-muted-foreground" />
                        <span className="font-medium text-xs">{log.user_name || '\u2014'}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${actionColor(log.action)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      <span className="font-mono">{log.entity_type}</span>
                      {log.entity_id && <span className="text-[10px] ml-1">#{log.entity_id.slice(0, 8)}</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground max-w-[200px] truncate">
                      {log.details ? JSON.stringify(log.details).slice(0, 80) : '\u2014'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </main>
      </div>
    </CollapsibleLayout>
  );
}
